import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, abs, money, errMsg, TYPE_LABELS } from "../../lib/api";
import { Loading } from "../../components/PageHeader";
import { useAuth } from "../../context/AppContext";
import { AdminTitle, Pill, ErrorBox } from "./AdminLayout";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

function DeleteButton({ p, onDone }) {
  const del = async () => {
    try {
      const { data } = await api.delete(`/admin/products/${p.id}`);
      toast.success(data.archived ? "Product archived (it has past buyers)" : "Product deleted");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger className="p-2 muted hover:text-red-400" data-testid={`admin-product-delete-${p.slug}`} aria-label="Delete"><Trash2 size={15} /></AlertDialogTrigger>
      <AlertDialogContent className="rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Delete “{p.title}”?</AlertDialogTitle>
          <AlertDialogDescription>It will be removed from the shop. Past buyers keep access to their files.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction className="rounded-none bg-red-600 hover:bg-red-700 text-white" onClick={del} data-testid="confirm-delete-button">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const PRODUCT_COLS = [["thumb", ""], ["title", "Title"], ["type", "Type"], ["price", "Price"], ["stock", "Stock"], ["status", "Status"], ["actions", ""]];

export default function AdminProducts() {
  const { can } = useAuth();
  const canEdit = can("manage_products");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/admin/products", { params: { q: q || undefined, type: type || undefined } })
    .then((r) => { setItems(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [q, type]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-products">
      <AdminTitle eyebrow="Catalogue" title="Products">
        {canEdit && <Link to="/admin/products/new" className="btn-gold" data-testid="admin-new-product-button"><Plus size={14} /> New product</Link>}
      </AdminTitle>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <input className="wb-input" placeholder="Search by title…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-product-search-input" />
        <select className="wb-input sm:!w-56" value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-product-type-filter">
          <option value="">All types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {err ? <ErrorBox msg={err} /> : !items ? <Loading /> : items.length === 0 ? <p className="muted py-10" data-testid="admin-products-empty">No products found.</p> : (
        <div className="overflow-x-auto border wb-line">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b wb-line text-left">
                {PRODUCT_COLS.map(([k, h]) => <th key={k} className="px-4 py-3 font-mono text-[0.62rem] uppercase tracking-[0.2em] font-normal muted">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b wb-line transition-colors hover:bg-wb-card" data-testid={`admin-product-row-${p.slug}`}>
                  <td className="px-4 py-3">{p.image_urls[0] ? <img src={abs(p.image_urls[0])} alt="" className="h-12 w-12 object-cover" /> : <div className="h-12 w-12 bg-wb-bg2" />}</td>
                  <td className="px-4 py-3"><Link to={`/admin/products/${p.id}`} className="font-display text-lg hover:text-wb-gold">{p.title}</Link>{p.featured && <span className="ml-2"><Pill tone="gold">Featured</Pill></span>}</td>
                  <td className="px-4 py-3 muted">{TYPE_LABELS[p.type]}</td>
                  <td className="px-4 py-3 font-mono">{money(p.price)}</td>
                  <td className="px-4 py-3 font-mono" data-testid={`admin-product-stock-${p.slug}`}>{p.type === "physical_art" ? (p.stock > 0 ? p.stock : <Pill tone="red">Sold out</Pill>) : "—"}</td>
                  <td className="px-4 py-3">{p.active ? <Pill tone="green">Published</Pill> : <Pill>Draft</Pill>}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Link to={`/admin/products/${p.id}`} className="inline-block p-2 muted hover:text-wb-gold" data-testid={`admin-product-edit-${p.slug}`} aria-label="Edit"><Pencil size={15} /></Link>
                    {canEdit && <DeleteButton p={p} onDone={load} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
